import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { ICustomer } from '@/types/models/Customer';
import { customerAPI } from '@/modules/common/services/http/customer';
import { StorageKeys } from '@/modules/common/services/local-storage';
import {
  getAccountStates,
  AccountStates,
  getRegulatoryUpdateStatus,
  RegulatoryUpdateStatus
} from '../../services/http/account';
import { isEmpty } from 'lodash-es';

@Module({
  namespaced: true,
  name: 'CustomerModule'
})
export class CustomerStoreModule extends VuexModule {
  private customer?: ICustomer = undefined;
  private accountStatus?: AccountStates = undefined;
  private redirectToMobileModalOpen = false;
  public isImpersonated = false;
  public regulatoryUpdateStatus?: RegulatoryUpdateStatus = undefined;

  @Mutation
  public setCustomer(value: ICustomer) {
    localStorage.setItem(StorageKeys.CustomerId, value._id);
    this.customer = value;
  }

  @Mutation
  public setAccountStatus(value: AccountStates) {
    this.accountStatus = value;
  }

  @Mutation
  public setIsImpersonated(value: boolean) {
    this.isImpersonated = value;
  }

  @Mutation
  public openRedirectToMobileModal() {
    this.redirectToMobileModalOpen = true;
  }

  @Mutation
  public closeRedirectToMobileModal() {
    this.redirectToMobileModalOpen = false;
  }

  @Mutation
  public resetCustomer() {
    localStorage.removeItem(StorageKeys.CustomerId);
    this.customer = undefined;
  }

  @Mutation
  public resetAccountStatus() {
    this.accountStatus = undefined;
  }

  @Mutation
  public setRegulatoryUpdateStatus(value: RegulatoryUpdateStatus) {
    this.regulatoryUpdateStatus = value;
  }

  public get getCustomer(): ICustomer | undefined {
    return this.customer;
  }

  public get getAccountStatus(): AccountStates | undefined {
    return this.accountStatus;
  }

  public get hasCustomer(): boolean {
    return !isEmpty(this.customer);
  }

  public get getCustomerId(): string {
    if (this.customer) return this.customer._id;
    return '';
  }

  public get getCustomerInitial(): string {
    if (this.customer) return this.customer.name.charAt(0);
    return '';
  }

  public get isCustomerPJ() {
    return !!this.customer?.professionalProfile;
  }

  public get isRedirectToMobileModalOpen() {
    return this.redirectToMobileModalOpen;
  }

  public get isRegulatoryStatusBlocked() {
    return this.regulatoryUpdateStatus === RegulatoryUpdateStatus.BLOCKED;
  }

  public get isRegulatoryStatusAboutToBeBlocked() {
    return (
      this.regulatoryUpdateStatus === RegulatoryUpdateStatus.ABOUT_TO_BE_BLOCKED
    );
  }

  @Action
  public async fetchRegulatoryUpdateStatus() {
    const customerId = this.context.getters.getCustomerId;
    const { status } = await getRegulatoryUpdateStatus(customerId);
    this.context.commit('setRegulatoryUpdateStatus', status);
  }

  @Action
  public async fetchCustomerAccountStatus() {
    const accountStatus = await getAccountStates();
    this.context.commit('setAccountStatus', accountStatus.state);
  }

  @Action({ rawError: true })
  public async fetchCustomer() {
    const { customer } = await customerAPI.getCustomer();
    this.context.commit('setCustomer', customer);
    this.context.dispatch('fetchRegulatoryUpdateStatus');
    this.context.dispatch('fetchCustomerAccountStatus');
  }
}
